import { useMemo } from 'react';
import { DayPicker } from 'react-day-picker';
import { format, isSameDay } from 'date-fns';
import type { Event } from '@/hooks/useEvents';
import { cn } from '@/lib/utils';
import 'react-day-picker/dist/style.css';

interface MiniCalendarProps {
  events: Event[];
  currentDate: Date;
  onDateSelect: (date: Date) => void;
}

export function MiniCalendar({ events, currentDate, onDateSelect }: MiniCalendarProps) {
  const daysWithEvents = useMemo(() => {
    const dateMap: Record<string, number> = {};
    events.forEach(event => {
      const date = format(new Date(event.start_time), 'yyyy-MM-dd');
      dateMap[date] = (dateMap[date] || 0) + 1;
    });
    return dateMap;
  }, [events]);

  const modifiers = {
    hasEvents: (date: Date) => {
      const dateStr = format(date, 'yyyy-MM-dd');
      return !!daysWithEvents[dateStr];
    },
  };

  const modifiersStyles = {
    hasEvents: {
      position: 'relative' as const,
    },
  };

  return (
    <div className="mini-calendar">
      <DayPicker
        mode="single"
        selected={currentDate}
        onSelect={(date) => date && onDateSelect(date)}
        modifiers={modifiers}
        modifiersStyles={modifiersStyles}
        className="rounded-md border p-3"
        classNames={{
          months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
          month: "space-y-4",
          caption: "flex justify-center pt-1 relative items-center",
          caption_label: "text-sm font-medium",
          nav: "space-x-1 flex items-center",
          nav_button: cn(
            "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"
          ),
          nav_button_previous: "absolute left-1",
          nav_button_next: "absolute right-1",
          table: "w-full border-collapse space-y-1",
          head_row: "flex",
          head_cell: "text-muted-foreground rounded-md flex-1 min-w-[2rem] font-normal text-[0.8rem] text-center",
          row: "flex w-full mt-2",
          cell: cn(
            "relative p-0 text-center text-sm flex-1 min-w-[2rem] focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent",
            "first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md"
          ),
          day: cn(
            "h-8 w-full aspect-square p-0 font-normal aria-selected:opacity-100 hover:bg-accent hover:text-accent-foreground rounded-md flex items-center justify-center"
          ),
          day_selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
          day_today: "bg-accent text-accent-foreground",
          day_outside: "text-muted-foreground opacity-50",
          day_disabled: "text-muted-foreground opacity-50",
          day_range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
          day_hidden: "invisible",
        }}
      />
      <style>{`
        .mini-calendar .rdp {
          --rdp-cell-size: 2rem;
          --rdp-accent-color: hsl(var(--primary));
        }
        
        .mini-calendar .rdp-table {
          width: 100%;
        }
        
        .mini-calendar .rdp-day_range_start,
        .mini-calendar .rdp-day_range_end {
          background-color: hsl(var(--primary));
          color: hsl(var(--primary-foreground));
        }
        
        .mini-calendar .rdp-day[data-has-events]::after {
          content: '';
          position: absolute;
          bottom: 2px;
          left: 50%;
          transform: translateX(-50%);
          width: 4px;
          height: 4px;
          border-radius: 50%;
          background: hsl(var(--primary));
        }
      `}</style>
    </div>
  );
}
